import { IViewFactoryConstructorParameter } from '#/CoreFrameInterfaces';
import Factory from '@/Factories/Factory';
import Utils from '@/Infrastructure/CustomBindingUtilities';
import ViewFactory from '@/Factories/ViewFactory';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import { CustomBindingRenderHooks, ICustomBinding } from '#/DefaultViewExtensionInterfaces';
import RADApplication from '@/Management/RADApplication'
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';

type Options = IViewFactoryConstructorParameter & {
    uiGenerator: string;
    hooks?: CustomBindingRenderHooks;
};

export default class ViewBinding implements ICustomBinding {

    public initBinding() {
        ko.bindingHandlers.view = {
            init: (element: HTMLElement, valueAccessor: () => Options, bindings: KnockoutAllBindingsAccessor, view: any, bindingContext: KnockoutBindingContext) => {
                let options = valueAccessor();
                let wrapper = $(element);
                let allBindings = bindings();

                let factory = Factory.createInstance(options.uiGenerator, options) as ViewFactory;
                if (factory == null) {
                    return;
                }

                Utils.registerDisposable(factory, bindings, bindingContext);

                this.triggerHook(options.hooks, 'beforeRender', factory, bindingContext);

                factory.async(() => {

                    const action = ($view: JQuery) => {
                        let newBindings = jQuery.extend({}, allBindings, { uiElement: factory });
                        delete newBindings['view'];

                        ko.applyBindingsToNode($view[0], newBindings);

                        $view.appendTo(wrapper);

                        this.triggerHook(options.hooks, 'afterRender', factory, bindingContext);

                        ko.utils.domNodeDisposal.addDisposeCallback(element, () => {
                            if (!factory.isDisposed && !(factory as any).__isDisposing) {
                                factory.dispose();
                            }
                            factory = null;
                        });
                    };

                    if (AsyncTypeGuards.isAsyncTupleProvider<any>(factory)) {
                        factory.getTupleAsync().then(tuple => {
                            action(tuple.view);
                        }).catch(e => {
                            this.handleError(e);
                            this.triggerHook(options.hooks, 'onFailure', factory, bindingContext, e);
                        });
                    } else {
                        try {
                            const tuple = factory.getTuple();
                            action(tuple.view);
                        } catch (e) {
                            this.handleError(e);
                            this.triggerHook(options.hooks, 'onFailure', factory, bindingContext, e);
                        }
                    }
                });
            }
        };
    }

    private handleError(error?: Error) {
        console.error(error);

        const errorMessageBox = MessageBoxViewModel.createErrorMessageBox(null, 'GENERIC_ERROR', error?.stack);
        RADApplication.getInstance().showMessageBox(errorMessageBox);
    }

    private triggerHook<T extends keyof CustomBindingRenderHooks<ViewFactory>>(
        hooks: CustomBindingRenderHooks<ViewFactory>,
        hookName: T,
        ...params: Parameters<CustomBindingRenderHooks<ViewFactory>[T]>
    ) {
        if (!hooks)
            return;

        const hook = hooks[hookName];
        if (!hook || typeof hook !== 'function')
            return;

        hook(params[0], params[1]);
    }
}